import { useState } from "react"
import { formatCurrency } from "src/shared/utils/formatCurrency"
import {
  Segment,
  Flight,
  ScheduledRange,
  dayCount,
  effectiveMix,
  fmtRange,
  payDays,
  pct2,
} from "./segmentEngine"
import { FloorNetwork, adGroupOnIn, agKey, minDailyFor, networkOnIn } from "./floors"
import { ENDED_TIP, InfoTip } from "./SegmentStrip"

/**
 * Bid Strategy no longer earns a column of its own: it is one of two short enum values, read
 * far less often than the money columns. It rides under the Bid value as a secondary label,
 * which also lets the money columns widen for their edit inputs.
 */
const HEADS = ["Network / Ad Group", "Percent", "Amount", "Daily", "Daily Cap", "Bid", "Active"]

const MPN1789_NOTE =
  "The funded-days basis is expected to change once MPN-1789 ships (spendable cash plus upcoming deposits over remaining active days). Today's figures reflect current production logic, not the corrected one."

export type SegmentAdGroup = FloorNetwork["adGroups"][number] & {
  bidLabel?: string
  bidValue?: string
  /** Already live on the network, so switching it off asks before it is even staged. */
  published?: boolean
  /** Raw, for the save payload. `bidValue` is formatted for the cell and would have to be
   *  parsed back out of "$8.00". */
  bidAmount?: number | null
  /**
   * This ad group's share of its network as the POLICY states it, 0-100. `share` is a ratio
   * of resolved dollars and the two only agree while every ad group's margin is 0, which is
   * every ad group in production today -- but a pause writes a policy, and a policy wants
   * the percent that was written, not one back-solved from the money it produced.
   */
  percentOfNetwork?: number
}

export type SegmentNetwork = Omit<FloorNetwork, "adGroups"> & {
  color: string
  glyph: string
  dark?: boolean
  strategy: string
  bid: string
  bidUnsupported?: boolean
  dailyCap: string
  /**
   * Raw cap and bid, for the save payload. `dailyCap` and `bid` are formatted for their
   * cells ("—", "$8.00") and parsing them back would be guesswork. These ride along so a
   * per-window override can carry the campaign's cap and bid forward: the override wins
   * over the campaign's policy outright, so writing one with these unset would drop a cap
   * on 494 of the 586 network rows in production.
   */
  dailyCapAmount?: number | null
  bidAmount?: number | null
  /** This network's serving ranges, from the budget tree. */
  scheduledRanges: readonly ScheduledRange[]
  adGroups: SegmentAdGroup[]
}

export type SegmentNetworksTableProps = {
  segment: Segment
  networks: SegmentNetwork[]
  defaultMix: Record<string, number>
  flight: Flight
  fundedDays: number
  /** The campaign's serving ranges, before any per-network narrowing. */
  scheduledRanges: readonly ScheduledRange[]
  editing: boolean
  pendingNet: Record<string, boolean>
  pendingAg: Record<string, boolean>
  onSetPercent: (key: string, pct: number) => void
  onSetSpend: (key: string, dollars: number) => void
  onSetCap: (key: string, value: string) => void
  /** Unsaved Daily Cap edits by network key, so the field shows what was typed. */
  capEdits?: Record<string, string>
  /** The selected window has already ended, so nothing in it can be edited. */
  locked?: boolean
  onUnpin: (key: string) => void
  onToggleNetwork: (key: string, on: boolean, wasOn: boolean) => void
  onToggleAdGroup: (groupKey: string, on: boolean, wasOn: boolean) => void
}

const has = (o: Record<string, boolean>, k: string) => Object.prototype.hasOwnProperty.call(o, k)

export function SegmentNetworksTable(props: SegmentNetworksTableProps) {
  const { segment, networks, defaultMix, fundedDays, locked } = props
  /* An elapsed window is read-only, so it is simply never "editing". Resolved once here
     rather than at each field: every input, switch and floor check below already keys off
     this one flag, and threading `locked` to each of them is how one gets missed. */
  const editing = props.editing && !locked
  const [open, setOpen] = useState<Record<string, boolean>>({})
  /** The ad group whose pause is being confirmed, and the other networks ticked to widen it. */
  const [confirmPause, setConfirmPause] = useState<
    { key: string; network: SegmentNetwork; group: SegmentAdGroup; also: string[] } | null
  >(null)

  const days = dayCount(segment.start, segment.end)
  // The campaign's own divisor, for the segment-wide figures. A network that narrows the
  // schedule gets its own below -- `pdFor` -- because it has fewer days to spend over.
  const pd = payDays(segment, props.scheduledRanges, fundedDays)
  const pdFor = (n: SegmentNetwork) => payDays(segment, n.scheduledRanges, fundedDays) || pd
  const capped = pd < days
  const mix = effectiveMix(segment, defaultMix)
  const spend = segment.amount
  const amountFor = (p: number) => (spend * p) / 100

  const shown = networks.filter((n) => n.isConfigured)
  const stateOn = (n: SegmentNetwork) =>
    has(props.pendingNet, n.key) ? props.pendingNet[n.key] : networkOnIn(segment, n)
  const committedOn = (n: SegmentNetwork) => networkOnIn(segment, n)
  const groupOn = (n: SegmentNetwork, g: SegmentAdGroup) => {
    const gk = agKey(n, g)
    return has(props.pendingAg, gk) ? props.pendingAg[gk] : adGroupOnIn(segment, n, g)
  }

  const live = shown.filter((n) => committedOn(n))
  const dim = shown.filter((n) => !committedOn(n))

  // A paused network holds no allocation, so it is out of the reconciliation.
  const total = shown.filter((n) => committedOn(n)).reduce((a, n) => a + (mix[n.key] || 0), 0)
  const unalloc = Math.max(0, 100 - total)
  const overPct = Math.max(0, total - 100)
  const over = overPct > 0.005

  const pinnedCount = shown.filter((n) => Object.prototype.hasOwnProperty.call(segment.pinned || {}, n.key)).length

  /**
   * A network below its floor will not serve, so the check is against real dollars per
   * funded day, not against 0%. Per ad group: a network at $8/day split evenly is two groups
   * at $4/day, and both fail a $5 floor while the network total looks fine.
   */
  const groupDaily = (n: SegmentNetwork, g: SegmentAdGroup) =>
    pdFor(n) > 0 ? (amountFor(mix[n.key] || 0) / pdFor(n)) * (g.share || 0) : 0
  const underGroups = (n: SegmentNetwork) =>
    minDailyFor(n.key) > 0 ? n.adGroups.filter((g) => groupOn(n, g) && groupDaily(n, g) < minDailyFor(n.key)) : []
  const under = shown.filter((n) => stateOn(n) && editing && underGroups(n).length > 0)

  const underMsg = (() => {
    if (under.length !== 1) return `${under.length} networks are below their daily minimum`
    const n = under[0]
    const gs = underGroups(n)
    const where =
      n.adGroups.length > 1
        ? `${n.name} · ${gs.length > 1 ? `${gs.length} ad groups are` : `${gs[0].name || "ad group"} is`}`
        : `${n.name} is`
    const rate = gs.length > 1 ? Math.min(...gs.map((x) => groupDaily(n, x))) : groupDaily(n, gs[0])
    return `${where} below the ${formatCurrency(minDailyFor(n.key))}/day minimum at ${formatCurrency(rate)}/day`
  })()

  /**
   * The ad group's other networks, as rows the pause can be widened to (MPN-1893, 10/8).
   *
   * Matched on the ad group's id, which is the same under every network it runs on. A
   * network already off in this window is left out: its ad groups are paused by the cascade
   * already, so ticking it would write a policy that changes nothing.
   */
  const otherNetworksFor = (n: SegmentNetwork, g: SegmentAdGroup) =>
    shown.filter(
      (other) =>
        other.key !== n.key &&
        committedOn(other) &&
        other.adGroups.some((x) => x.key === g.key && groupOn(other, x)),
    )

  const setAdGroup = (n: SegmentNetwork, g: SegmentAdGroup, on: boolean) => {
    if (!editing) return
    const key = agKey(n, g)
    // Two reasons to ask before this is even staged: the ad group is already serving, or it
    // runs elsewhere and the pause could be meant for all of it. Neither applies and the
    // toggle commits straight through, which is what an unpublished single-network ad group
    // did before.
    const widenable = otherNetworksFor(n, g).length > 0
    if (!on && groupOn(n, g) && (g.published || widenable)) {
      setConfirmPause({ key, network: n, group: g, also: [] })
      return
    }
    props.onToggleAdGroup(key, on, adGroupOnIn(segment, n, g))
  }

  /** Pause the ad group on the row's network and on every network ticked beside it. */
  const commitPause = () => {
    if (!confirmPause) return
    const { network, group, also } = confirmPause
    props.onToggleAdGroup(agKey(network, group), false, adGroupOnIn(segment, network, group))
    also.forEach((key) => {
      const other = shown.find((x) => x.key === key)
      const twin = other?.adGroups.find((x) => x.key === group.key)
      if (!other || !twin) return
      props.onToggleAdGroup(agKey(other, twin), false, adGroupOnIn(segment, other, twin))
    })
    setConfirmPause(null)
  }

  const renderNetwork = (n: SegmentNetwork, dimmed: boolean) => {
    const paused = !committedOn(n)
    const pend = has(props.pendingNet, n.key) ? props.pendingNet[n.key] : null
    const pinned = Object.prototype.hasOwnProperty.call(segment.pinned || {}, n.key)
    const belowFloor = editing && !paused && underGroups(n).length > 0
    const hot = over && !paused
    const pct = mix[n.key] || 0
    const amt = amountFor(pct)
    const isOpen = !!open[n.key]

    // Every ad group off reads as a paused network, not "0/N active"; the count stays for a
    // partial split.
    const zeroAg = n.adGroups.length > 0 && n.adGroups.every((g) => !groupOn(n, g))
    const onCount = n.adGroups.filter((g) => groupOn(n, g)).length
    const split = zeroAg || onCount === n.adGroups.length ? null : `${onCount}/${n.adGroups.length} active`

    return (
      <div key={n.key} className={`ntbl__net${dimmed ? " ntbl__net--dim" : ""}${hot ? " ntbl__net--hot" : ""}`}>
        <div
          className={`ntbl__row${pinned && !paused ? " ntbl__row--pinned" : ""}${hot ? " ntbl__row--hot" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => ({ ...o, [n.key]: !isOpen }))}
          onKeyDown={(e) => e.key === "Enter" && setOpen((o) => ({ ...o, [n.key]: !isOpen }))}
        >
          <div className="ntbl__cell ntbl__cell--name">
            <span className={`ntbl__chev${isOpen ? " ntbl__chev--open" : ""}`}>›</span>
            <span
              className="ntbl__glyph"
              style={{ background: n.color, color: n.dark ? "#111" : "#fff", fontSize: n.glyph.length > 1 ? 9 : 11 }}
            >
              {n.glyph}
            </span>
            <span className="ntbl__name">{n.name}</span>
            {(paused || zeroAg) && <span className="ntbl__pill ntbl__pill--paused">PAUSED</span>}
            {pend !== null && (
              <span className="ntbl__pill ntbl__pill--pending">
                {pend ? "Will resume on save" : "Will pause on save"}
              </span>
            )}
            {pinned && !dimmed && (
              <span className="ntbl__edited" title="Set by hand for this segment. New segments copy this value.">
                Edited
              </span>
            )}
            {pinned && editing && !dimmed && (
              <button
                type="button"
                className="ntbl__reset"
                title="Go back to the campaign default split."
                onClick={(e) => {
                  e.stopPropagation()
                  props.onUnpin(n.key)
                }}
              >
                Reset
              </button>
            )}
            {split && <span className="ntbl__pill ntbl__pill--split">{split}</span>}
          </div>

          {/* A paused network still shows its configured Percent/Amount/Daily here; only the
              child ad-group row zeroes out. */}
          <div className="ntbl__cell">
            {editing ? (
              <NumField
                value={pct}
                suffix="%"
                danger={hot || belowFloor}
                onCommit={(v) => props.onSetPercent(n.key, Math.min(100, v))}
              />
            ) : (
              <span className="ntbl__v">{pct2(pct)}%</span>
            )}
          </div>

          <div className="ntbl__cell">
            {editing ? (
              <NumField value={amt} prefix="$" danger={hot || belowFloor} onCommit={(v) => props.onSetSpend(n.key, v)} />
            ) : (
              <span className="ntbl__v ntbl__v--bold">{formatCurrency(amt)}</span>
            )}
          </div>

          {/* Editing Daily commits daily x funded days, so Amount and Percent follow. */}
          <div className="ntbl__cell">
            {editing ? (
              <NumField
                value={amt / pdFor(n)}
                prefix="$"
                danger={hot || belowFloor}
                onCommit={(v) => props.onSetSpend(n.key, v * pdFor(n))}
              />
            ) : (
              <span className="ntbl__v">{formatCurrency(amt / pdFor(n))}</span>
            )}
          </div>

          <div className="ntbl__cell">
            {editing ? (
              <CapField
                value={props.capEdits?.[n.key] ?? n.dailyCap}
                onCommit={(v) => props.onSetCap(n.key, v)}
              />
            ) : (
              <InfoTip
                info="A ceiling on the daily figure sent to the network, not the figure itself."
                formula="sent to the network = min(frac{Amount}{funded days}, Daily Cap)"
                method="Networks pace within a day and can spend above an even share of it. The cap bounds what they are given; unset, they receive the full computed daily figure."
              >
                {n.dailyCap}
              </InfoTip>
            )}
          </div>

          <div className="ntbl__cell">
            <BidCell
              strategy={n.strategy}
              value={
                n.bidUnsupported ? (
                  <InfoTip
                    info={`Google sets the bid automatically under ${n.strategy}. There is no manual bid field to publish, so Motimatic leaves it empty and controls spend with the daily amount instead.`}
                  >
                    Not supported
                  </InfoTip>
                ) : (
                  <span className="ntbl__v ntbl__v--muted">{n.bid}</span>
                )
              }
            />
          </div>

          <div className="ntbl__cell ntbl__cell--active">
            <ActiveSwitch
              disabled={!editing}
              on={pend !== null ? pend : !paused}
              onChange={(v) => editing && props.onToggleNetwork(n.key, v, committedOn(n))}
            />
          </div>
        </div>

        {isOpen &&
          n.adGroups.map((g) => {
            const gk = agKey(n, g)
            const on = groupOn(n, g)
            const gPend = has(props.pendingAg, gk) ? props.pendingAg[gk] : null
            const gAmt = amt * g.share

            return (
              <div key={g.name}>
                <div className={`ntbl__row ntbl__row--child${pinned && !paused ? " ntbl__row--pinned" : ""}`}>
                  <div className="ntbl__cell ntbl__cell--name ntbl__cell--indent">
                    <span className="ntbl__group-name">{g.name}</span>
                    {!paused && !on && <span className="ntbl__pill ntbl__pill--paused">PAUSED</span>}
                    {gPend !== null && (
                      <span className="ntbl__pill ntbl__pill--pending">
                        {gPend ? "Will resume on save" : "Will pause on save"}
                      </span>
                    )}
                  </div>

                  {/* A paused network's ad group still renders real numbers, just zeroed. The
                      "not running" copy is for a group turned off under a live network, which
                      is a different, ad-group-scoped state. */}
                  {!paused && !on ? (
                    <div className="ntbl__cell ntbl__cell--span">
                      <InfoTip info={`This ad group is switched off for ${n.name}; its share is unallocated.`}>
                        {`— Paused on ${n.name} for this ad group`}
                      </InfoTip>
                    </div>
                  ) : (
                    <>
                      <div className="ntbl__cell">
                        <span className="ntbl__v ntbl__v--muted">{paused ? "0.00" : pct2(pct * g.share)}%</span>
                      </div>
                      <div className="ntbl__cell">
                        <span className="ntbl__v">{formatCurrency(paused ? 0 : gAmt)}</span>
                      </div>
                      <div className="ntbl__cell">
                        <span className="ntbl__v">{formatCurrency(paused ? 0 : gAmt / pdFor(n))}</span>
                      </div>
                      <div className="ntbl__cell">
                        <span className="ntbl__v ntbl__v--faint">—</span>
                      </div>
                      <div className="ntbl__cell">
                        <BidCell
                          strategy={g.bidLabel}
                          value={
                            <span className={`ntbl__v${g.bidValue && !paused ? "" : " ntbl__v--faint"}`}>
                              {paused ? "—" : g.bidValue || "—"}
                            </span>
                          }
                        />
                      </div>
                    </>
                  )}

                  <div className="ntbl__cell ntbl__cell--active">
                    <ActiveSwitch
                      disabled={!editing}
                      on={gPend !== null ? gPend : !!on}
                      onChange={(v) => setAdGroup(n, g, v)}
                    />
                  </div>
                </div>

                {confirmPause?.key === gk && (
                  <div className="ntbl__confirm">
                    <div className="ntbl__confirm-body">
                      <span>
                        <strong>! </strong>Pause {g.name} on {n.name}?
                        {g.published ? " This ad group is already live — it pauses on Save." : ""}
                      </span>

                      {/* The ad group's other networks, so one gesture can pause all of it
                          (10/8). The row's own network is shown ticked and fixed: it is the
                          toggle that opened this, and un-ticking it would leave a dialog
                          that does nothing. */}
                      {otherNetworksFor(n, g).length > 0 && (
                        <div className="ntbl__confirm-nets">
                          <span className="ntbl__confirm-nets-label">
                            Also pause {g.name} on
                          </span>
                          <label className="ntbl__confirm-net">
                            <input type="checkbox" checked readOnly disabled />
                            <span>{n.name}</span>
                          </label>
                          {otherNetworksFor(n, g).map((other) => (
                            <label className="ntbl__confirm-net" key={other.key}>
                              <input
                                type="checkbox"
                                checked={confirmPause.also.indexOf(other.key) >= 0}
                                onChange={(e) =>
                                  setConfirmPause((c) =>
                                    !c
                                      ? c
                                      : {
                                          ...c,
                                          also: e.target.checked
                                            ? c.also.concat([other.key])
                                            : c.also.filter((k) => k !== other.key),
                                        },
                                  )
                                }
                              />
                              <span>{other.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <span className="ntbl__confirm-actions">
                      <button type="button" className="seg-btn seg-btn--sm" onClick={() => setConfirmPause(null)}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="seg-btn seg-btn--sm seg-btn--danger"
                        onClick={commitPause}
                      >
                        Pause
                      </button>
                    </span>
                  </div>
                )}
              </div>
            )
          })}
      </div>
    )
  }

  return (
    <div className="ntbl">
      <div className="ntbl__bar">
        <span className="ntbl__title">Networks</span>
        <span className="ntbl__sub">
          {fmtRange(segment.start, segment.end)} · {days} days
          {capped && <span title={MPN1789_NOTE}>{` · ${fundedDays} funded`}</span>} ·{" "}
          {formatCurrency(spend)} media spend
        </span>
        {/* The table still renders, it just cannot be changed: an elapsed window's dollars
            have already been spent. Said here rather than on every disabled field. */}
        {locked && (
          <span className="ntbl__ended" title={ENDED_TIP}>
            <i className="bi bi-lock-fill" />
            Ended · view only
          </span>
        )}
        <div className="ntbl__bar-right">
          {over && <span className="ntbl__flag ntbl__flag--bad">! {pct2(overPct)}% over-allocated</span>}
          {/* Danger red, not amber: a daily floor is a hard serve-blocking threshold. */}
          {under.length > 0 && (
            <span className="ntbl__flag ntbl__flag--bad">
              ! {underMsg}
              <InfoTip
                info="A network below its floor will not serve at all, so this blocks Save."
                formula="ad group daily = frac{segment × network %}{funded days} × ad-group share"
                method="Checked per ad group, not per network total: a network at $8/day split evenly is two groups at $4/day, and both fail a $5 floor while the network looks healthy. Meta and Pinterest $1/day; Google and Snapchat $5/day."
              />
            </span>
          )}
          <span className="ntbl__sub">
            {pinnedCount ? `${pinnedCount} edited for this segment` : ""}
          </span>
        </div>
      </div>

      {/* One actionable line; the why and the two fixes live behind the (i). */}
      {capped && (
        <div className="ntbl__notice">
          <span className="ntbl__notice-bang">!</span>
          <span className="ntbl__notice-text">
            {fundedDays} of {days} days funded — daily amounts reflect the shorter window.
          </span>
          <InfoTip
            info="Why the daily figures are higher than the segment's length implies."
            formula={`Daily = frac{Amount}{${fundedDays} funded days}  not  frac{Amount}{${days} days}`}
            method={`Shorten the segment, or extend the replenish cycle on the contract's budget, to spend across its full length. ${MPN1789_NOTE}`}
          />
        </div>
      )}

      <div className="ntbl__scroll">
        <div className={`ntbl__grid${editing ? " ntbl__grid--edit" : ""}`}>
          <div className="ntbl__head">
            {HEADS.map((h, i) => (
              <div key={h} className={`ntbl__th${i === 0 ? " ntbl__th--left" : ""}${i === 6 ? " ntbl__th--active" : ""}`}>
                {h}
                {h === "Daily" && (
                  <InfoTip
                    info="What each network receives per day in this segment."
                    formula={`Daily = frac{Amount}{${pd} ${capped ? "funded days" : "days"}}`}
                    method={
                      capped
                        ? `The segment runs ${days} days but only ${fundedDays} are funded, so the divisor is the shorter window. ${MPN1789_NOTE}`
                        : "The divisor is the segment's own length, capped by Daily Cap where one is set."
                    }
                  />
                )}
              </div>
            ))}
          </div>

          {live.map((n) => renderNetwork(n, false))}

          {dim.length > 0 && (
            <div className="ntbl__group-head">
              <span className="ntbl__group-label">Inactive Networks</span>
              <span className="ntbl__sub">{dim.length} not serving · still editable</span>
            </div>
          )}
          {dim.map((n) => renderNetwork(n, true))}

          <div className="ntbl__row ntbl__row--total">
            <div className="ntbl__cell ntbl__cell--name">Total</div>
            <div className="ntbl__cell">
              <span className="ntbl__v ntbl__v--bold">{pct2(total)}%</span>
            </div>
            <div className="ntbl__cell">
              <span className="ntbl__v ntbl__v--bold">{formatCurrency(amountFor(total))}</span>
            </div>
            <div className="ntbl__cell">
              <span className="ntbl__v ntbl__v--bold">{formatCurrency(amountFor(total) / pd)}</span>
            </div>
            <div className="ntbl__cell" />
            <div className="ntbl__cell" />
            <div className="ntbl__cell ntbl__cell--active" />
          </div>

          {/*
              Three states, not two (design 10/9).

              Balanced is worth saying out loud -- a row of zeroes reads the same as a row
              nobody has looked at, so it gets a green check instead.

              Unallocated money is RED text and nothing else: no fill, no bang, no Save
              block. It is money that will not spend, which is a mistake worth naming, but
              it is not the operator doing something forbidden. Over-allocation is, so it
              keeps the fill and the bang. Amber used to carry the under case and read as a
              softer version of the same thing; these are different kinds of wrong.
          */}
          <div className={`ntbl__row ntbl__row--resid${over ? " ntbl__row--hot" : ""}`}>
            {!over && unalloc < 0.005 ? (
              <>
                <div className="ntbl__cell ntbl__cell--name ntbl__resid--ok">
                  <i className="bi bi-check-circle-fill" />
                  Fully allocated
                </div>
                <div className="ntbl__cell" />
                <div className="ntbl__cell" />
                <div className="ntbl__cell" />
              </>
            ) : (
              <>
                <div className="ntbl__cell ntbl__cell--name ntbl__resid--bad">
                  {over ? (
                    <>
                      <strong>!&nbsp;</strong>Over-allocated
                    </>
                  ) : (
                    <>
                      Segment unallocated
                      <InfoTip info="Part of this segment's amount is not assigned to a network. It will not spend." />
                    </>
                  )}
                </div>
                {[
                  pct2(over ? overPct : unalloc) + "%",
                  formatCurrency(amountFor(over ? overPct : unalloc)),
                  formatCurrency(amountFor(over ? overPct : unalloc) / pd),
                ].map((v, i) => (
                  <div className="ntbl__cell" key={i}>
                    <span className="ntbl__resid--bad">{v}</span>
                  </div>
                ))}
              </>
            )}
            <div className="ntbl__cell" />
            <div className="ntbl__cell" />
            <div className="ntbl__cell ntbl__cell--active" />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Bid value with its strategy as a small label underneath — the strategy explains the value. */
function BidCell({ value, strategy }: { value: React.ReactNode; strategy?: string }) {
  return (
    <span className="ntbl__bid">
      <span className="ntbl__bid-value">{value}</span>
      {strategy && <span className="ntbl__bid-strategy">{strategy}</span>}
    </span>
  )
}

function ActiveSwitch({
  on,
  onChange,
  disabled,
}: {
  on: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={on}
      aria-label={on ? "Switch off" : "Switch on"}
      className={`ntbl__switch${on ? " ntbl__switch--on" : ""}`}
      onClick={(e) => {
        e.stopPropagation()
        onChange(!on)
      }}
    >
      <span className="ntbl__switch-knob" />
    </button>
  )
}

/**
 * Mixes derive from real dollars, so percents are fractional (14.29%, not 14%). The field
 * edits as a draft string: committing the raw float clips in the cell, and stripping "."
 * would turn 14.2857 into 142857.
 */
function NumField({
  value,
  prefix,
  suffix,
  danger,
  onCommit,
}: {
  value: number
  prefix?: string
  suffix?: string
  danger?: boolean
  onCommit: (v: number) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft !== null ? draft : value.toFixed(2)

  return (
    <span className={`seg-field${danger ? " seg-field--danger" : ""}`}>
      {prefix && <span className="seg-field__prefix">{prefix}</span>}
      <input
        value={shown}
        inputMode="decimal"
        style={{ width: prefix ? 58 : 42 }}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const clean = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
          setDraft(clean)
          const n = parseFloat(clean)
          onCommit(isNaN(n) ? 0 : Math.max(0, n))
        }}
        onBlur={() => setDraft(null)}
      />
      {suffix && <span className="seg-field__prefix">{suffix}</span>}
    </span>
  )
}

/** Daily Cap is a stored figure, not derived, so it commits the raw string. */
function CapField({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft !== null ? draft : value === "—" ? "" : value

  return (
    <span
      className="seg-field"
      title="Ceiling on the daily figure sent to the network — not the figure itself."
    >
      <span className="seg-field__prefix">$</span>
      <input
        value={shown}
        inputMode="decimal"
        placeholder="—"
        style={{ width: 42 }}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const clean = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
          setDraft(clean)
          onCommit(clean === "" ? "—" : clean)
        }}
        onBlur={() => setDraft(null)}
      />
    </span>
  )
}
